import React, { useContext, useEffect, useState, useMemo } from 'react';
import FilterDropdownType from '../../enums/filterDropdownType.enum.js';
import { EditOutlined, EnvironmentOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { Button, Tag, Modal } from 'antd';
import { listFacilities, deleteFacility } from "../../services/facility.service.js";
import { listProductTypes } from "../../services/productType.service.js";  
import './adminFacilities.scss';
import { PageHeader } from '../pageHeader/pageHeader.component';
import { AdminLocationModal } from '../adminLocationModal/adminLocationModal.component';
import ObjectType from '../../enums/objectType.enum';
import { FlexibleTable } from '../flexibleTable/flexibleTable.component.js';
import { UserContext } from '../../contexts/user.context.js';
import { FacilityForm } from "./facilityForm.component.js";
import FacilityStatus from '../../enums/facilityStatus.enum.js';
import Role from '../../enums/role.enum.js';
import PayTo from '../../enums/payTo.enum.js';
const select = '_id name location menu status fax phone website';
const populate = [{
  path: 'menu', 
  populate: 'productType'
}]

export const AdminFacilities = () => {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState();
  const [productTypes, setProductTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAdminLocationModalOpen, setIsAdminLocationModalOpen] = useState();
  const [filteredCount, setFilteredCount] = useState();
  const { setCounts, currentUser } = useContext(UserContext);
  const [filteredFacilities, setFilteredFacilities] = useState(facilities);
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    document.title = 'Instalab | Facilities'

    const fetchFacilities = async () => {
      setIsLoading(true);
      let params = {
        select,
        filter: {},
        populate,
        sort: 'name'
      };
  
      const fetchedFacilities = await listFacilities(params);
      setFacilities(fetchedFacilities);
      setFilteredCount(fetchedFacilities.length);
      setFilteredFacilities(fetchedFacilities);
      setIsLoading(false);
    };
  
    const fetchProductTypes = async () => {
      const productTypes = await listProductTypes({ 
        filter: { isActive: true, hasFacility: true },
        sort: 'title' 
      });
      setProductTypes(productTypes);
    };

    fetchFacilities();
    fetchProductTypes();
  }, []);


  // Update how we create the filters
  const productTypeFilters = useMemo(() => {
    const uniqueTypes = new Map();
    
    facilities.forEach(facility => {
      facility.menu?.forEach(item => {
        if (item.productType?._id && item.productType?.title) {
          uniqueTypes.set(
            item.productType._id.toString(),
            {
              label: item.productType.title,
              value: item.productType._id.toString()
            }
          );
        }
      });
    });
    
    return Array.from(uniqueTypes.values())
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [facilities]);

  const handleDeleteFacility = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this facility?',
      content: 'This action cannot be undone.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteFacility(id);
          setFacilities(facilities.filter(facility => facility._id !== id));
          setFilteredCount(prevCount => prevCount - 1);
          setCounts(prevCounts => ({
            ...prevCounts,
            facilities: prevCounts.facilities - 1
          }));
        } catch (error) {
          console.error('Failed to delete facility:', error);
        }
      }
    });
  };

  const getActionItems = ({ _id }) => [
    {
      key: '1',
      label: (
        <Button onClick={() => {
          setFacilityId(_id);
          setIsEditModalOpen(true);
        }}>
          <EditOutlined /> Edit
        </Button>
      )
    },
    {
      key: '2',
      label: (
        <Button onClick={() => {
          setFacilityId(_id);
          setIsAdminLocationModalOpen(true);
        }}>
          <EnvironmentOutlined /> Set Location
        </Button>
      )
    },
    currentUser.role === Role.ADMIN && {
      type: 'divider'
    },
    currentUser.role === Role.ADMIN && {
      key: '3',
      label: (
        <Button
          danger
          onClick={() => handleDeleteFacility(_id)}
        >
          <DeleteOutlined /> Remove
        </Button>
      )
    }
  ];

  const formatLocation = (location, fax, phone)  => {
    if (!location) return null
    return <>
      {location.streetAddress}{location.streetAddress2 ? ' ' + location.streetAddress2 : ''}, {location.city}, {location.state} {location.postalCode}
      <br/>
      {formatContact(fax, phone)}
    </>
  };

  const formatContact = (fax, phone) => {
    if (!fax && !phone) return '-';
    return <>
      {fax && <>Fax: {fax}<br/></>}
      {phone && <>Phone: {phone}</>}
    </>
  };


  // Render the status with colored tags
  const renderStatus = (status) => {
    const color = status === FacilityStatus.ACTIVE ? 'green' : 'default';
    return <Tag color={color}>{status}</Tag>;
  };
  

  const renderMenu = (menu) => {
    if (!menu || menu.length === 0) return '-';
    
    return (
      <div className="facility-menu">
        {menu.map((item, index) => (
          <div key={index} className="menu-item">
            <span className="product-title">{item.productType?.title}</span>
            <span className="price">{item.payTo === PayTo.INSURANCE ? 'Insurance' : `$${item.price}`}</span>
          </div>
        ))}
      </div>
    );
  };


  const getCustomFilter = (facility, value) => {
    return {
      name: () => facility.name && facility.name.toLowerCase().includes(value.toLowerCase()),
      location: () => facility.location?.city && facility.location.city.toLowerCase().includes(value.toLowerCase()),
      menu: () => facility.menu?.some(menuItem => menuItem.productType?._id.toString() === value.toString())
    }
  }
  
 
  return (
    <div className="facility-facilities">
      <PageHeader
        title='Facilities'
        count={filteredCount}
        actions={(
          <Button
            type='primary'
            onClick={() => {
              setFacilityId(null);
              setIsEditModalOpen(true);
            }}
          >
            + Add New Facility
          </Button>
        )}
      />

      <AdminLocationModal
        objectId={facilityId}
        objectType={ObjectType.FACILITY}
        open={isAdminLocationModalOpen}
        setOpen={setIsAdminLocationModalOpen}
        select={select}
        populate={populate}
        onSuccess={facility => {
          setFacilities(facilities.map(p => p._id === facilityId ? facility : p));
        }}
      />

      <FacilityForm
        facilityId={facilityId}
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        onSuccess={facility => {
          if (facilityId) {
            setFacilities(facilities.map(f => f._id === facilityId ? facility : f));
            setFilteredFacilities(filteredFacilities.map(f => f._id === facilityId ? facility : f));
          } else {
            setFacilities([facility, ...facilities]);
            setFilteredCount(cachedFilterCount => cachedFilterCount + 1);
            setCounts(cachedCounts => {
              return {
                ...cachedCounts,
                facilities: cachedCounts.facilities + 1
              };
            });
          }
        }}
      />

      <FlexibleTable
        isLoading={isLoading}
        records={filteredFacilities}
        setFilteredCount={setFilteredCount}
        getActionItems={getActionItems}
        getCustomFilter={getCustomFilter}
        columns={[
          {
            title: 'Facility',
            dataIndex: 'name',
            filterDropdownType: FilterDropdownType.INPUT,
          },
          {
            title: 'Menu',
            dataIndex: 'menu',
            render: menu => renderMenu(menu),
            filterDropdownType: FilterDropdownType.CHECKBOX,
            filterOptions: productTypeFilters,
            onFilter: (value, record) => {
              if (!record.menu || !Array.isArray(record.menu)) {
                return false;
              }
              
              const hasMatches = record.menu.some(menuItem => {
                const matches = menuItem.productType?._id.toString() === value.toString();
                return matches;
              });

              return hasMatches;
            },
          },
          {
            title: 'Location Details',
            dataIndex: 'location',
            render: (location, {fax, phone}) => formatLocation(location, fax, phone),
            filterDropdownType: FilterDropdownType.INPUT
          },
 

          {
            title: 'Status',
            dataIndex: 'status',
            render: renderStatus,
          },
        ]}
        onChange={(pagination, filters, sorter) => {
          console.log('Table change:', { filters });
          setActiveFilters(filters.menu || []);
          
          if (filters.menu && filters.menu.length > 0) {
            const newFilteredData = facilities.filter(record => {
              return filters.menu.some(filterValue => {
                return record.menu?.some(menuItem => 
                  menuItem.productType?._id.toString() === filterValue.toString()
                );
              });
            });
            setFilteredFacilities(newFilteredData);
          } else {
            setFilteredFacilities(facilities);
          }
        }}
      />
    </div>
  );
};